// Generic "scroll to first invalid field" helper, reused by every form in this
// app. It never references specific field names — it walks the DOM looking for
// whatever is currently marked invalid (aria-invalid="true", or an "error"/"invalid"
// class token, which covers this project's field-error/rf-error/is-invalid/p-invalid
// conventions) and scrolls/focuses/highlights it. Call it right after a failed
// v$.$validate() in any component's submit handler:
//
//   this.v$.$validate();
//   if (this.v$.$error) { scrollToFirstError(); return; }

const ERROR_QUERY_SELECTOR = '[aria-invalid="true"], [class*="error"], [class*="invalid"]';
const ERROR_CLASS_TOKEN = /(^|[\s_-])(error|invalid)([\s_-]|$)/i;

const FOCUSABLE_SELECTOR = [
  'input:not([type="hidden"]):not([disabled]):not([hidden])',
  'textarea:not([disabled]):not([hidden])',
  'select:not([disabled]):not([hidden])',
  'button:not([disabled]):not([hidden])',
  '[tabindex]:not([tabindex="-1"]):not([hidden])',
  '[contenteditable="true"]',
  // Custom controls built as a <label for="..."> wrapping a visually-hidden
  // input (e.g. a file upload dropzone) — not natively focusable, but still
  // the right thing to scroll to/highlight as the field's container.
  'label[for]',
].join(', ');

const HIGHLIGHT_CLASS = 'scroll-error-highlight';
const MAX_CONTAINER_CLIMB = 5;
const HIGHLIGHT_DURATION_MS = 1600;

function isVisible(el) {
  return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

function isErrorIndicator(el) {
  if (el.getAttribute('aria-invalid') === 'true') return true;
  const cls = el.getAttribute('class');
  return !!cls && ERROR_CLASS_TOKEN.test(cls);
}

function findFirstErrorElement(root) {
  const candidates = root.querySelectorAll(ERROR_QUERY_SELECTOR);
  for (const el of candidates) {
    if (isErrorIndicator(el) && isVisible(el)) return el;
  }
  return null;
}

function firstVisibleFocusable(scope) {
  const found = scope.querySelectorAll(FOCUSABLE_SELECTOR);
  for (const el of found) {
    if (isVisible(el)) return el;
  }
  return null;
}

// Error indicators are sometimes the invalid control itself (aria-invalid input),
// sometimes a wrapper that carries the error class around a control it contains
// (e.g. "rf-field--error"), sometimes just a message with the control as a
// sibling somewhere up the tree (e.g. "field-error"). Check self, then
// descendants, then climb.
function findFocusTarget(errorEl) {
  if (errorEl.matches(FOCUSABLE_SELECTOR) && isVisible(errorEl)) {
    return { container: errorEl, focusEl: errorEl };
  }
  const nested = firstVisibleFocusable(errorEl);
  if (nested) {
    return { container: errorEl, focusEl: nested };
  }
  let node = errorEl;
  for (let i = 0; i < MAX_CONTAINER_CLIMB && node.parentElement; i++) {
    node = node.parentElement;
    const focusEl = firstVisibleFocusable(node);
    if (focusEl) return { container: node, focusEl };
  }
  return { container: errorEl, focusEl: null };
}

/**
 * Scrolls to, focuses, and briefly highlights the first invalid field found
 * under `root`. Returns true if an error was found and handled.
 */
export function scrollToFirstError(root = document) {
  const errorEl = findFirstErrorElement(root);
  if (!errorEl) return false;

  const { container, focusEl } = findFocusTarget(errorEl);

  container.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if (focusEl && typeof focusEl.focus === 'function') {
    // Let the smooth scroll kick off first so focus() doesn't trigger its own
    // (instant) scroll and fight the smooth one.
    requestAnimationFrame(() => focusEl.focus({ preventScroll: true }));
  }

  container.classList.add(HIGHLIGHT_CLASS);
  window.setTimeout(() => container.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_DURATION_MS);

  return true;
}
